import { useMemo, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Checkbox,
  Tag,
  TagGroup,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowUploadRegular } from '@fluentui/react-icons';

import { getBasisCategories } from '../../helpers';
import { TReviewBasis } from '../../types';

const useStyles = makeStyles({
  picker: {
    display: 'grid',
    gridTemplateColumns: '220px minmax(0, 1fr)',
    height: '260px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
      height: 'auto',
    },
  },
  categories: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingVerticalXS,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    overflowY: 'auto',
    '@media (max-width: 900px)': {
      flexDirection: 'row',
      flexWrap: 'wrap',
      borderRight: 'none',
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  category: {
    justifyContent: 'space-between',
    fontWeight: tokens.fontWeightRegular,
  },
  categoryActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    overflowY: 'auto',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    paddingLeft: '32px',
    marginTop: '-4px',
  },
  selection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  placeholder: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  bases: TReviewBasis[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  uploadedName?: string;
  onUpload?: () => void;
};

// Category on the left, what is available inside it on the right, selection as
// removable tags below.
export const ReviewBasisPicker = ({ bases, selectedIds, onChange, uploadedName, onUpload }: TProps) => {
  const classes = useStyles();
  const categories = useMemo(() => getBasisCategories(bases), [bases]);
  const [activeCategory, setActiveCategory] = useState(categories[0]);

  const visibleBases = bases.filter((basis) => basis.category === (activeCategory ?? categories[0]));
  const selectedSet = new Set(selectedIds);

  const toggle = (basisId: string) =>
    onChange(selectedSet.has(basisId) ? selectedIds.filter((id) => id !== basisId) : [...selectedIds, basisId]);

  const countInCategory = (category: string) =>
    bases.filter((basis) => basis.category === category && selectedSet.has(basis.id)).length;

  return (
    <div className="flex flex-col gap-3">
      <div className={classes.picker}>
        <div className={classes.categories}>
          {categories.map((category) => {
            const count = countInCategory(category);
            const isActive = category === (activeCategory ?? categories[0]);
            return (
              <Button
                key={category}
                appearance="subtle"
                className={`${classes.category} ${isActive ? classes.categoryActive : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                <span>{category}</span>
                {count > 0 ? (
                  <Badge appearance="filled" color="brand" size="small">
                    {count}
                  </Badge>
                ) : null}
              </Button>
            );
          })}
        </div>

        <div className={classes.options}>
          {visibleBases.map((basis) => (
            <div key={basis.id} className={classes.option}>
              <Checkbox label={basis.label} checked={selectedSet.has(basis.id)} onChange={() => toggle(basis.id)} />
              {basis.description ? <Caption1 className={classes.description}>{basis.description}</Caption1> : null}
            </div>
          ))}

          {visibleBases.some((basis) => basis.id === 'uploaded-pdf') && onUpload ? (
            <Button appearance="secondary" icon={<ArrowUploadRegular />} className="w-fit ml-8 mt-1" onClick={onUpload}>
              {uploadedName ?? 'Add file'}
            </Button>
          ) : null}
        </div>
      </div>

      {selectedIds.length > 0 ? (
        <TagGroup
          className={classes.selection}
          onDismiss={(_, data) => onChange(selectedIds.filter((id) => id !== data.value))}
        >
          {selectedIds.map((id) => (
            <Tag key={id} value={id} shape="rounded" dismissible dismissIcon={{ 'aria-label': 'remove' }}>
              {bases.find((basis) => basis.id === id)?.label ?? id}
            </Tag>
          ))}
        </TagGroup>
      ) : (
        <Body1 className={classes.placeholder}>Nothing selected</Body1>
      )}
    </div>
  );
};
